/**
 * Pixel-Perfect Category Management & Category Product Detail View.
 * Matches exact reference UI design for Category Dashboard & Category Detail Product Grid.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { siteConfig } from "@/config/site";
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  Check,
  Search,
  LayoutGrid,
  Tag,
  Eye,
  SlidersHorizontal,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Download,
  Copy,
  Star,
  ArrowLeft,
  MoreVertical,
  List,
  FolderTree,
} from "lucide-react";
import {
  fetchAdminCategoriesFromSupabase,
  syncCategoryToSupabase,
  deleteCategoryFromSupabase,
  resolveCategoryImage,
  type CategoryItem,
} from "@/features/admin/categories/store/admin-categories-store";
import {
  fetchAdminProductsFromSupabase,
  
  
  syncProductToSupabase,
  deleteProductFromSupabase,
  type ExtendedProduct,
} from "@/features/admin/products/store/admin-products-store";
import { CategoryFormDialog } from "@/components/admin/categories/category-form-dialog";
import { ProductFormDialog } from "@/components/admin/products/product-form-dialog";
import { getProductSlug } from "@/lib/products";

function isCategoryProductMatch(catName: string, prodCat: string): boolean {
  if (!catName || !prodCat) return false;
  const normCat = catName.toLowerCase().trim();
  const normProd = prodCat.toLowerCase().trim();

  return normProd === normCat;
}

export const Route = createFileRoute("/admin/categories")({
  validateSearch: (search: Record<string, unknown>): { category?: string } => ({
    category: (search.category as string) || (search.cat as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: `Product Categories — Admin — ${siteConfig.name}` }],
  }),
  component: AdminCategoriesPage,
});

function AdminCategoriesPage() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [allProducts, setAllProducts] = useState<ExtendedProduct[]>([]);

  // Navigation State: null = main categories table, CategoryItem = category detail view
  const [selectedCategory, setSelectedCategory] = useState<CategoryItem | null>(null);

  useEffect(() => {
    if (searchParams.category && categories.length > 0) {
      const match = categories.find(
        (c) =>
          c.slug.toLowerCase() === searchParams.category?.toLowerCase() ||
          c.name.toLowerCase() === searchParams.category?.toLowerCase() ||
          c.id === searchParams.category,
      );
      if (match) {
        setSelectedCategory(match);
      }
    } else if (!searchParams.category) {
      setSelectedCategory(null);
    }
  }, [searchParams.category, categories]);

  const handleSelectCategory = (cat: CategoryItem | null) => {
    setSelectedCategory(cat);
    setDetailCurrentPage(1);
    if (cat) {
      navigate({
        search: { category: cat.slug || cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") },
        replace: false,
      });
    } else {
      navigate({
        search: {},
        replace: false,
      });
    }
  };

  // Category Dashboard Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  // Category Detail Page Filters
  const [productSearch, setProductSearch] = useState("");
  const [productStatusFilter, setProductStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">(
    "ALL",
  );
  const [productStockFilter, setProductStockFilter] = useState<"ALL" | "IN_STOCK" | "OUT_OF_STOCK">(
    "ALL",
  );
  const [productSort, setProductSort] = useState<"NEWEST" | "PRICE_LOW" | "PRICE_HIGH" | "NAME">(
    "NEWEST",
  );
  const [detailViewMode, setDetailViewMode] = useState<"grid" | "list">("grid");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [detailCurrentPage, setDetailCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const detailItemsPerPage = 8;

  // Modal States
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [editingProduct, setEditingProduct] = useState<ExtendedProduct | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<CategoryItem | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<ExtendedProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const loadCategoriesData = () => {
    fetchAdminCategoriesFromSupabase().then((list) => {
      if (list && list.length > 0) setCategories(list);
    });

    // Fetch live products
    fetchAdminProductsFromSupabase().then((prods) => {
      if (prods && prods.length > 0) setAllProducts(prods);
    });
  };

  useEffect(() => {
    loadCategoriesData();
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const updateCategoryStore = (newList: CategoryItem[]) => {
    setCategories(newList);
  };

  const handleOpenCreateCategory = () => {
    setEditingCategory(null);
    setFormOpen(true);
  };

  const handleOpenEditCategory = (item: CategoryItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingCategory(item);
    setFormOpen(true);
  };

  const handleDeleteCategory = (item: CategoryItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeletingCategory(item);
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deletingCategory) return;
    setIsDeleting(true);
    try {
      const itemId = deletingCategory.id;
      const categoryName = deletingCategory.name;

      // 1. Reassign products in this category to "Uncategorized" (Option 1: Safe preservation)
      const affectedProducts = allProducts.filter(
        (p) => (p.category || "").toLowerCase().trim() === categoryName.toLowerCase().trim(),
      );

      if (affectedProducts.length > 0) {
        const updatedProducts = allProducts.map((p) => {
          if ((p.category || "").toLowerCase().trim() === categoryName.toLowerCase().trim()) {
            const up = { ...p, category: "Uncategorized" };
            syncProductToSupabase(up).catch(console.warn);
            return up;
          }
          return p;
        });
        setAllProducts(updatedProducts);
      }

      // 2. Remove category
      const updatedCategories = categories.filter((c) => c.id !== itemId);
      updateCategoryStore(updatedCategories);
      if (selectedCategory?.id === itemId) {
        handleSelectCategory(null);
      }
      await deleteCategoryFromSupabase(itemId);

      if (affectedProducts.length > 0) {
        triggerToast(
          `Category "${categoryName}" deleted. ${affectedProducts.length} product(s) moved to "Uncategorized".`,
        );
      } else {
        triggerToast(`Category "${categoryName}" deleted successfully.`);
      }
    } catch (err: unknown) {
      console.error("Delete category failed:", err);
      const message = (err as { message?: string }).message;
      triggerToast(`Failed to delete category: ${message || "Database error"}`);
      loadCategoriesData();
    } finally {
      setIsDeleting(false);
      setDeletingCategory(null);
    }
  };

  const handleDisplayOrderChange = async (id: string, newOrder: number) => {
    const updated = categories.map((c) => (c.id === id ? { ...c, displayOrder: newOrder } : c));
    updateCategoryStore(updated);
    const target = updated.find((c) => c.id === id);
    if (target) {
      try {
        await syncCategoryToSupabase(target);
      } catch (err) {
        console.error("Order change failed:", err);
      }
    }
  };

  const handleToggleCategoryStatus = async (category: CategoryItem) => {
    const prevCategories = [...categories];
    const newStatus = category.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updated = categories.map((c) =>
      c.id === category.id ? { ...c, status: newStatus as CategoryItem["status"] } : c,
    );
    updateCategoryStore(updated);
    if (selectedCategory?.id === category.id) {
      setSelectedCategory({ ...selectedCategory, status: newStatus as CategoryItem["status"] });
    }

    try {
      await syncCategoryToSupabase({ ...category, status: newStatus as CategoryItem["status"] });
      triggerToast("Category status updated.");
    } catch (err: unknown) {
      console.error("Status update failed:", err);
      const message = (err as { message?: string }).message;
      updateCategoryStore(prevCategories);
      triggerToast(`Failed to update status in database: ${message}`);
    }
  };

  const handleToggleStatus = async (id: string) => {
    const target = categories.find((c) => c.id === id);
    if (target) {
      await handleToggleCategoryStatus(target);
    }
  };

  const handleSaveCategoryForm = async (formData: Partial<CategoryItem>) => {
    const prevCategories = [...categories];
    try {
      if (editingCategory) {
        const updated = categories.map((c) =>
          c.id === editingCategory.id ? ({ ...c, ...formData } as CategoryItem) : c,
        );
        updateCategoryStore(updated);
        if (selectedCategory?.id === editingCategory.id) {
          setSelectedCategory({ ...selectedCategory, ...formData } as CategoryItem);
        }
        await syncCategoryToSupabase({ ...editingCategory, ...formData });
        triggerToast(`Category "${formData.name}" updated successfully.`);
      } else {
        const newCat: CategoryItem = {
          id: `cat-${Date.now()}`,
          name: formData.name || "New Category",
          slug: (formData.name || "category").toLowerCase().replace(/[^a-z0-9]+/g, "-"),
          parentId: null,
          description: formData.description || "",
          image: formData.image || "/plantpicture/p1/5.webp",
          displayOrder: categories.length + 1,
          status: "ACTIVE",
          productsCount: 0,
          seoTitle: `${formData.name} | Aster Decorz`,
          seoDescription: formData.description || "",
          seoKeywords: "plants, category",
          createdAt: new Date().toISOString(),
        };
        const updated = [...categories, newCat];
        updateCategoryStore(updated);
        await syncCategoryToSupabase(newCat);
        triggerToast(`Category "${newCat.name}" created successfully.`);
      }
    } catch (err: unknown) {
      console.error("Save category failed:", err);
      const message = (err as { message?: string }).message;
      updateCategoryStore(prevCategories);
      triggerToast(`Failed to save category in database: ${message}`);
    }
  };

  // Product Actions in Category Detail View
  const handleSaveProduct = async (productData: Partial<ExtendedProduct>) => {
    const finalProductData: Partial<ExtendedProduct> = {
      ...productData,
      category: productData.category || selectedCategory?.name || "Indoor Plants",
    };

    const prevProducts = [...allProducts];
    const existingIdx = allProducts.findIndex((p) => p.id === finalProductData.id);
    let updated: ExtendedProduct[];
    if (existingIdx >= 0) {
      updated = [...allProducts];
      updated[existingIdx] = { ...updated[existingIdx], ...finalProductData } as ExtendedProduct;
    } else {
      updated = [finalProductData as ExtendedProduct, ...allProducts];
    }
    setAllProducts(updated);

    try {
      await syncProductToSupabase(finalProductData);
      triggerToast(`Product "${finalProductData.name || "Item"}" saved successfully.`);
      setProductDialogOpen(false);
      setEditingProduct(null);
    } catch (err: unknown) {
      console.error("Save product to Supabase failed:", err);
      const message = (err as { message?: string }).message;
      setAllProducts(prevProducts);
      triggerToast(`Failed to save product in database: ${message}`);
    }
  };

  const handleDuplicateProduct = async (product: ExtendedProduct) => {
    const duplicated: ExtendedProduct = {
      ...product,
      id: `p-${Date.now()}`,
      name: `${product.name} (Copy)`,
      sku: `${product.sku}-COPY`,
    };
    const updated = [duplicated, ...allProducts];
    setAllProducts(updated);
    await syncProductToSupabase(duplicated);
    triggerToast(`Duplicated "${product.name}".`);
  };

  const handleDeleteProduct = (product: ExtendedProduct, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeletingProduct(product);
  };

  const handleConfirmDeleteProduct = async () => {
    if (!deletingProduct) return;
    setIsDeleting(true);
    try {
      const productId = deletingProduct.id;
      const updated = allProducts.filter((p) => p.id !== productId);
      setAllProducts(updated);
      await deleteProductFromSupabase(productId);
      triggerToast(`Product "${deletingProduct.name}" deleted successfully.`);
    } catch (err) {
      console.error("Delete product failed:", err);
    } finally {
      setIsDeleting(false);
      setDeletingProduct(null);
    }
  };


  // Filtered Products for selected category
  const filteredCategoryProducts = useMemo(() => {
    if (!selectedCategory) return [];
    const catName = selectedCategory.name.toLowerCase();

    const matched = allProducts.filter((p) => {
      const matchesCategory = isCategoryProductMatch(catName, p.category);

      const matchesSearch =
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.id.toLowerCase().includes(productSearch.toLowerCase());

      const matchesStatus =
        productStatusFilter === "ALL"
          ? true
          : productStatusFilter === "ACTIVE"
            ? p.status === "PUBLISHED" || p.inStock
            : p.status === "DRAFT" || !p.inStock;

      const matchesStock =
        productStockFilter === "ALL"
          ? true
          : productStockFilter === "IN_STOCK"
            ? (p.stockQuantity ?? (p.inStock ? 50 : 0)) > 0
            : (p.stockQuantity ?? 0) === 0;

      return matchesCategory && matchesSearch && matchesStatus && matchesStock;
    });

    const finalProducts = matched;

    return finalProducts.sort((a, b) => {
      if (productSort === "PRICE_LOW") return a.price - b.price;
      if (productSort === "PRICE_HIGH") return b.price - a.price;
      if (productSort === "NAME") return a.name.localeCompare(b.name);
      return 0;
    });
  }, [
    selectedCategory,
    allProducts,
    productSearch,
    productStatusFilter,
    productStockFilter,
    productSort,
  ]);

  // Metrics calculation
  const metrics = useMemo(() => {
    const total = categories.length;
    const active = categories.filter((c) => c.status === "ACTIVE").length;
    const totalProducts =
      allProducts.length || categories.reduce((sum, c) => sum + (c.productsCount || 0), 0);
    return { total, active, totalProducts };
  }, [categories, allProducts]);

  // Filtered Categories List
  const filteredCategories = useMemo(() => {
    return categories.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL"
          ? true
          : statusFilter === "ACTIVE"
            ? c.status === "ACTIVE"
            : c.status !== "ACTIVE";

      return matchesSearch && matchesStatus;
    });
  }, [categories, searchQuery, statusFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage) || 1;
  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCategories.slice(start, start + itemsPerPage);
  }, [filteredCategories, currentPage, itemsPerPage]);

  const detailTotalPages = Math.ceil(filteredCategoryProducts.length / detailItemsPerPage) || 1;
  const paginatedDetailProducts = useMemo(() => {
    const start = (detailCurrentPage - 1) * detailItemsPerPage;
    return filteredCategoryProducts.slice(start, start + detailItemsPerPage);
  }, [filteredCategoryProducts, detailCurrentPage, detailItemsPerPage]);

  return (
    <div className="space-y-6 pb-16 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-sm font-semibold px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-2.5 animate-in fade-in duration-200">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* ======================================================== */}
      {/* SCENARIO A: CATEGORY DETAIL VIEW (MATCHING MOCKUP IMAGE) */}
      {/* ======================================================== */}
      {selectedCategory ? (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleSelectCategory(null)}
                  className="p-2 -ml-2 rounded-xl text-muted-foreground hover:text-emerald-800 hover:bg-secondary transition-colors cursor-pointer"
                  title="Back to All Categories"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                  {selectedCategory.name}
                </h1>
                <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs font-bold px-3 py-1 rounded-full">
                  {
                    allProducts.filter((p) =>
                      isCategoryProductMatch(selectedCategory.name, p.category),
                    ).length
                  }{" "}
                  Products
                </span>
              </div>
              {selectedCategory.description && (
                <p className="mt-1 text-sm text-muted-foreground font-medium ml-8">
                  {selectedCategory.description}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => triggerToast("Exported category products catalogue.")}
                className="inline-flex items-center gap-2 border border-border bg-card hover:bg-secondary text-foreground text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-xl shadow-2xs transition-colors"
              >
                <Download className="h-4 w-4" /> Export
              </button>

              <button
                onClick={() => {
                  setEditingProduct(null);
                  setProductDialogOpen(true);
                }}
                className="inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-md active:scale-95"
              >
                <Plus className="h-4 w-4" /> Add Product
              </button>

              <button
                onClick={() => handleOpenEditCategory(selectedCategory)}
                className="inline-flex items-center gap-1.5 border border-border bg-card hover:bg-secondary rounded-xl text-foreground text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 transition-colors shadow-2xs"
                title="Edit Category Settings"
              >
                <Edit2 className="h-4 w-4" /> Edit
              </button>

              <button
                onClick={() => handleDeleteCategory(selectedCategory)}
                className="inline-flex items-center gap-1.5 border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/60 rounded-xl text-red-600 dark:text-red-400 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 transition-colors shadow-2xs cursor-pointer"
                title="Delete Category"
              >
                <Trash2 className="h-4 w-4" /> Delete Category
              </button>
            </div>
          </div>

          {/* Category Filter & Search Control Bar */}
          <div className="bg-card border border-border/90 p-3.5 rounded-2xl shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
              {/* Search Products */}
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-xl text-xs font-medium focus:outline-none focus:ring-1 focus:ring-emerald-800"
                />
              </div>

              {/* Status Filter Dropdown */}
              <select
                value={productStatusFilter}
                onChange={(e) =>
                  setProductStatusFilter(e.target.value as "ALL" | "ACTIVE" | "INACTIVE")
                }
                className="bg-background border border-border text-xs font-semibold px-3 py-2 rounded-xl text-foreground focus:outline-none"
              >
                <option value="ALL">Status: All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>

              {/* Stock Filter Dropdown */}
              <select
                value={productStockFilter}
                onChange={(e) =>
                  setProductStockFilter(e.target.value as "ALL" | "IN_STOCK" | "OUT_OF_STOCK")
                }
                className="bg-background border border-border text-xs font-semibold px-3 py-2 rounded-xl text-foreground focus:outline-none"
              >
                <option value="ALL">Stock: All</option>
                <option value="IN_STOCK">In Stock</option>
                <option value="OUT_OF_STOCK">Out of Stock</option>
              </select>

              {/* Sort Dropdown */}
              <select
                value={productSort}
                onChange={(e) =>
                  setProductSort(e.target.value as "NEWEST" | "PRICE_LOW" | "PRICE_HIGH" | "NAME")
                }
                className="bg-background border border-border text-xs font-semibold px-3 py-2 rounded-xl text-foreground focus:outline-none hidden md:block"
              >
                <option value="NEWEST">Sort: Newest</option>
                <option value="PRICE_LOW">Price: Low to High</option>
                <option value="PRICE_HIGH">Price: High to Low</option>
                <option value="NAME">Name: A to Z</option>
              </select>
            </div>

            {/* Grid / List View Toggle */}
            <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-xl border">
              <button
                onClick={() => setDetailViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${
                  detailViewMode === "grid"
                    ? "bg-background text-emerald-800 shadow-2xs font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Grid View"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDetailViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${
                  detailViewMode === "list"
                    ? "bg-background text-emerald-800 shadow-2xs font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="List View"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* 4-COLUMN PRODUCT CARDS GRID (EXACT MATCH OF REFERENCE IMAGE) */}
          {filteredCategoryProducts.length === 0 ? (
            <div className="text-center py-16 bg-card border border-dashed rounded-3xl p-8 space-y-3">
              <Package className="h-12 w-12 text-muted-foreground mx-auto" />
              <h3 className="text-lg font-bold text-foreground">No Products Found</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                No products currently matched your filter criteria in "{selectedCategory.name}".
              </p>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setProductDialogOpen(true);
                }}
                className="mt-2 inline-flex items-center gap-2 bg-emerald-800 text-white font-bold text-xs uppercase px-5 py-2.5 rounded-xl"
              >
                <Plus className="h-4 w-4" /> Add Product to {selectedCategory.name}
              </button>
            </div>
          ) : detailViewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {paginatedDetailProducts.map((p, i) => {
                const stockQty = p.stockQuantity ?? (p.inStock ? 24 : 0);
                const isFeatured = i % 3 === 0;

                return (
                  <div
                    key={p.id}
                    className="bg-card border border-border/90 rounded-2xl p-4 shadow-xs hover:shadow-md transition-[color,background-color,border-color,box-shadow,transform,opacity] flex flex-col justify-between group"
                  >
                    <div>
                      {/* Card Image Area with Backdrop */}
                      <div className="relative h-48 w-full bg-stone-100 dark:bg-stone-900 rounded-xl overflow-hidden flex items-center justify-center p-3">
                        <input
                          type="checkbox"
                          className="absolute top-3 left-3 h-4 w-4 rounded border-border text-emerald-800 focus:ring-emerald-800 cursor-pointer"
                        />

                        {isFeatured && (
                          <span className="absolute top-3 right-3 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-2xs">
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Featured
                          </span>
                        )}

                        <img
                          src={p.image}
                          alt={p.name}
                          className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />

                        {/* Status Badge */}
                        <span
                          className={`absolute bottom-3 left-3 text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                            stockQty > 0
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                              : "bg-stone-200 text-stone-700"
                          }`}
                        >
                          {stockQty > 0 ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="mt-3.5 space-y-1">
                        <h4 className="font-bold text-sm text-foreground truncate group-hover:text-emerald-800 transition-colors">
                          {p.name}
                        </h4>

                        <div className="flex items-center justify-between">
                          <span className="font-display font-bold text-base text-foreground">
                            ₹{p.price.toLocaleString()}
                          </span>

                          <span
                            className={`text-xs font-semibold ${
                              stockQty > 0 ? "text-emerald-700" : "text-destructive"
                            }`}
                          >
                            Stock: {stockQty}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Row Bar (Eye, Edit, Copy, Delete) */}
                    <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between gap-1">
                      <Link
                        to="/product/$id"
                        params={{ id: getProductSlug(p) }}
                        target="_blank"
                        className="p-2 text-muted-foreground hover:text-emerald-800 hover:bg-secondary rounded-xl transition-colors"
                        title="View Product Page"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>

                      <button
                        onClick={() => {
                          setEditingProduct(p);
                          setProductDialogOpen(true);
                        }}
                        className="p-2 text-muted-foreground hover:text-emerald-800 hover:bg-secondary rounded-xl transition-colors"
                        title="Edit Product"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDuplicateProduct(p)}
                        className="p-2 text-muted-foreground hover:text-emerald-800 hover:bg-secondary rounded-xl transition-colors"
                        title="Duplicate Product"
                      >
                        <Copy className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteProduct(p)}
                        className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors"
                        title="Delete Product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* LIST VIEW */
            <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y">
              {paginatedDetailProducts.map((p) => (
                <div
                  key={p.id}
                  className="p-4 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-14 w-14 object-cover rounded-xl border bg-secondary shrink-0"
                    />
                    <div className="min-w-0">
                      <h4 className="font-bold text-base text-foreground truncate">{p.name}</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        SKU: {p.sku || p.id}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 shrink-0">
                    <span className="font-bold text-base text-foreground">₹{p.price}</span>
                    <span className="text-xs font-semibold text-emerald-700">
                      Stock: {p.stockQuantity || 24}
                    </span>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingProduct(p);
                          setProductDialogOpen(true);
                        }}
                        className="p-2 border rounded-xl text-xs font-bold text-foreground hover:bg-secondary"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p)}
                        className="p-2 text-muted-foreground hover:text-destructive rounded-xl"
                        title="Delete Product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Footer */}
          <div className="p-4 bg-card border border-border rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-semibold text-muted-foreground">
            <div>
              Showing <strong className="text-foreground">{(detailCurrentPage - 1) * detailItemsPerPage + 1}</strong> to{" "}
              <strong className="text-foreground">
                {Math.min(detailCurrentPage * detailItemsPerPage, filteredCategoryProducts.length)}
              </strong>{" "}
              of <strong className="text-foreground">{filteredCategoryProducts.length}</strong>{" "}
              products
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setDetailCurrentPage(Math.max(1, detailCurrentPage - 1))}
                disabled={detailCurrentPage === 1}
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-border text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {Array.from({ length: detailTotalPages }).map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setDetailCurrentPage(i + 1)}
                  className={`h-8 w-8 rounded-lg text-xs font-bold transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                    i + 1 === detailCurrentPage
                      ? "bg-emerald-800 text-white shadow-xs"
                      : "border border-border hover:bg-secondary text-foreground"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setDetailCurrentPage(Math.min(detailTotalPages, detailCurrentPage + 1))}
                disabled={detailCurrentPage === detailTotalPages}
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-border text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ======================================================== */
        /* SCENARIO B: CATEGORIES MAIN DASHBOARD (PREVIOUS TURN UI) */
        /* ======================================================== */
        <>
          {/* TOP BAR: Title & Header Controls */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                Product Categories
              </h1>
              <p className="mt-1 text-sm text-muted-foreground font-medium">
                Manage and organize your product categories
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
              {/* Search Box */}
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search categories..."
                  className="w-full pl-10 pr-4 py-2.5 bg-card border border-border/80 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-800/20 shadow-2xs"
                />
              </div>

              {/* Filter Button Toggle */}
              <button
                onClick={() =>
                  setStatusFilter((prev) =>
                    prev === "ALL" ? "ACTIVE" : prev === "ACTIVE" ? "INACTIVE" : "ALL",
                  )
                }
                className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-xs font-bold uppercase tracking-wider transition-colors shadow-2xs ${
                  statusFilter !== "ALL"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-card text-foreground border-border/80 hover:bg-secondary"
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>{statusFilter === "ALL" ? "Filters" : statusFilter}</span>
              </button>


              {/* Primary Add Button */}
              <button
                onClick={handleOpenCreateCategory}
                className="inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-md active:scale-95 shrink-0 cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                <span>Add Category</span>
              </button>
            </div>
          </div>

          {/* KPI STATS CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 p-5 rounded-2xl flex items-center gap-4 shadow-2xs">
              <div className="h-12 w-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/60 flex items-center justify-center shrink-0">
                <LayoutGrid className="h-6 w-6 text-emerald-700 dark:text-emerald-300" />
              </div>
              <div>
                <p className="text-xs font-bold text-emerald-800/80 dark:text-emerald-300/80">
                  Total Categories
                </p>
                <h3 className="font-display text-2xl font-bold text-foreground">{metrics.total}</h3>
                <p className="text-[11px] text-muted-foreground">All categories</p>
              </div>
            </div>

            <div className="bg-blue-50/60 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 p-5 rounded-2xl flex items-center gap-4 shadow-2xs">
              <div className="h-12 w-12 rounded-2xl bg-blue-100 dark:bg-blue-900/60 flex items-center justify-center shrink-0">
                <Tag className="h-6 w-6 text-blue-700 dark:text-blue-300" />
              </div>
              <div>
                <p className="text-xs font-bold text-blue-800/80 dark:text-blue-300/80">
                  Active Categories
                </p>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  {metrics.active}
                </h3>
                <p className="text-[11px] text-muted-foreground">Visible on store</p>
              </div>
            </div>

            <div className="bg-amber-50/60 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/40 p-5 rounded-2xl flex items-center gap-4 shadow-2xs">
              <div className="h-12 w-12 rounded-2xl bg-amber-100 dark:bg-amber-900/60 flex items-center justify-center shrink-0">
                <Package className="h-6 w-6 text-amber-700 dark:text-amber-300" />
              </div>
              <div>
                <p className="text-xs font-bold text-amber-800/80 dark:text-amber-300/80">
                  Total Products
                </p>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  {metrics.totalProducts}
                </h3>
                <p className="text-[11px] text-muted-foreground">In all categories</p>
              </div>
            </div>
          </div>

          {/* CATEGORY MANAGEMENT DATA TABLE */}
          <div className="bg-card border border-border/90 rounded-2xl shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="py-4 px-6">Category</th>
                    <th className="py-4 px-6">Description</th>
                    <th className="py-4 px-6 text-center">Products</th>
                    <th className="py-4 px-6 text-center">Status</th>
                    <th className="py-4 px-6 text-center">Display Order</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60 text-sm font-medium">
                  {paginatedCategories.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-muted-foreground">
                        <p className="text-sm font-semibold">
                          No categories found matching your query.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginatedCategories.map((cat) => {
                      const matchingProduct = allProducts.find((p) =>
                        isCategoryProductMatch(cat.name, p.category),
                      );
                      const catThumbnail = cat.image || matchingProduct?.image || "";
                      const finalCount = allProducts.filter((p) =>
                        isCategoryProductMatch(cat.name, p.category),
                      ).length;

                      return (
                        <tr
                          key={cat.id}
                          className="hover:bg-secondary/20 transition-colors group cursor-pointer"
                          onClick={() => handleSelectCategory(cat)}
                        >
                          {/* CATEGORY */}
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3.5">
                              {catThumbnail ? (
                                <img
                                  src={catThumbnail}
                                  alt={cat.name}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                  }}
                                  className="h-12 w-12 object-cover rounded-xl border bg-secondary shrink-0 shadow-2xs group-hover:scale-105 transition-transform"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                                  <FolderTree className="h-6 w-6" />
                                </div>
                              )}
                              <div>
                                <h4 className="font-bold text-base text-foreground group-hover:text-emerald-800 transition-colors">
                                  {cat.name}
                                </h4>
                                <p className="text-xs text-muted-foreground font-mono">
                                  /{cat.slug}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* DESCRIPTION */}
                          <td className="py-4 px-6 max-w-xs">
                            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                              {cat.description || "—"}
                            </p>
                          </td>

                          {/* PRODUCTS */}
                          <td className="py-4 px-6 text-center">
                            <span className="font-bold text-sm text-foreground bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2.5 py-1 rounded-full">
                              {finalCount}
                            </span>
                          </td>

                          {/* STATUS */}
                          <td className="py-4 px-6 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleStatus(cat.id);
                              }}
                              className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full transition-colors cursor-pointer ${
                                cat.status === "ACTIVE"
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                  : "bg-secondary text-muted-foreground hover:bg-stone-200"
                              }`}
                            >
                              {cat.status === "ACTIVE" ? "Active" : "Inactive"}
                            </button>
                          </td>

                          {/* DISPLAY ORDER */}
                          <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1.5">
                              <GripVertical className="h-4 w-4 text-muted-foreground/60 cursor-grab" />
                              <input
                                type="number"
                                value={cat.displayOrder}
                                onChange={(e) =>
                                  handleDisplayOrderChange(cat.id, parseInt(e.target.value) || 1)
                                }
                                className="w-14 text-center py-1 bg-background border border-border rounded-lg text-xs font-bold focus:outline-none focus:ring-1 focus:ring-emerald-800"
                              />
                            </div>
                          </td>

                          {/* ACTIONS */}
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={(e) => handleOpenEditCategory(cat, e)}
                                className="p-2 text-muted-foreground hover:text-emerald-800 hover:bg-secondary rounded-xl transition-colors"
                                title="Edit Category"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCategory(cat);
                                }}
                                className="p-2 text-muted-foreground hover:text-emerald-800 hover:bg-secondary rounded-xl transition-colors cursor-pointer"
                                title="View Products in Category"
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              <button
                                onClick={(e) => handleDeleteCategory(cat, e)}
                                className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors"
                                title="Delete Category"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* TABLE FOOTER & PAGINATION */}
            <div className="p-4 border-t border-border bg-card flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-semibold text-muted-foreground">
              <div>
                Showing{" "}
                <strong className="text-foreground">
                  {filteredCategories.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
                </strong>{" "}
                to{" "}
                <strong className="text-foreground">
                  {Math.min(currentPage * itemsPerPage, filteredCategories.length)}
                </strong>{" "}
                of <strong className="text-foreground">{filteredCategories.length}</strong>{" "}
                categories
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: totalPages }).map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 rounded-lg text-xs font-bold transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                        currentPage === page
                          ? "bg-emerald-800 text-white shadow-xs"
                          : "border border-border hover:bg-secondary text-foreground"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Category Add/Edit Form Dialog */}
      <CategoryFormDialog
        open={formOpen}
        categoryItem={editingCategory}
        parentCategories={[]}
        defaultParentId={null}
        onClose={() => setFormOpen(false)}
        onSave={handleSaveCategoryForm}
      />

      {/* Product Add/Edit Form Dialog */}
      <ProductFormDialog
        open={productDialogOpen}
        product={editingProduct}
        defaultCategory={selectedCategory?.name}
        onClose={() => {
          setProductDialogOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
      />

      {/* CUSTOM LUXURY DELETE CATEGORY CONFIRMATION MODAL */}
      {deletingCategory && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-7 max-w-md w-full shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-border/80 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-950/60 border border-red-200 dark:border-red-900/60 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0 shadow-xs">
                  <Trash2 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground">
                    Delete Category
                  </h3>
                  <p className="text-xs text-muted-foreground">Permanent taxonomy removal</p>
                </div>
              </div>
              <button
                onClick={() => setDeletingCategory(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {(() => {
              const affectedCount = allProducts.filter((p) =>
                isCategoryProductMatch(deletingCategory.name, p.category),
              ).length;

              return (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Are you sure you want to delete category{" "}
                    <strong className="text-foreground font-semibold">
                      "{deletingCategory.name}"
                    </strong>
                    ?
                  </p>
                  {affectedCount > 0 ? (
                    <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 p-3.5 rounded-xl text-xs text-emerald-900 dark:text-emerald-300 flex items-start gap-2.5">
                      <span className="font-bold shrink-0">Safe Preservation:</span>
                      <span>
                        All{" "}
                        <strong>
                          {affectedCount} product{affectedCount > 1 ? "s" : ""}
                        </strong>{" "}
                        currently under this category will not be lost. They will be safely moved to{" "}
                        <strong>"Uncategorized"</strong>.
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      This empty category will be permanently removed from your catalog and
                      database.
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeletingCategory(null)}
                disabled={isDeleting}
                className="px-4 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-foreground hover:bg-secondary transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteCategory}
                disabled={isDeleting}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-sm active:scale-95 disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {isDeleting ? (
                  "Deleting..."
                ) : (
                  <>
                    <Trash2 className="h-3.5 w-3.5" /> Delete Category
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM LUXURY DELETE PRODUCT CONFIRMATION MODAL */}
      {deletingProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-7 max-w-md w-full shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-border/80 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-950/60 border border-red-200 dark:border-red-900/60 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0 shadow-xs">
                  <Trash2 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground">Delete Product</h3>
                  <p className="text-xs text-muted-foreground">Permanent product removal</p>
                </div>
              </div>
              <button
                onClick={() => setDeletingProduct(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              Are you sure you want to delete{" "}
              <strong className="text-foreground font-semibold">"{deletingProduct.name}"</strong>?
              This item will be permanently removed from your catalog and database.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeletingProduct(null)}
                disabled={isDeleting}
                className="px-4 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-foreground hover:bg-secondary transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteProduct}
                disabled={isDeleting}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-sm active:scale-95 disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {isDeleting ? (
                  "Deleting..."
                ) : (
                  <>
                    <Trash2 className="h-3.5 w-3.5" /> Delete Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
